import { WorkflowElement } from "../../element"
import { SVGPointConverter } from "../../views/graph/svg/svg-point-converter"
import { determineMergingEdges } from "../../views/graph/utils"

/**
 * Defines workflow-svg element. Responsible for iterating over all edges of a
 * graph and translating the points provided by `Layout` into actual `path`
 * elements. All edges are modified as needed depending on the `kind` they're
 * drawing. For example, drawing edges of a condition node require knowing where
 * the `yes` and `no` labels are positioned so we draw lines to/from them.
 */
export class SVGCanvas extends WorkflowElement {
  static targetEdgeTranslation = 30
  static siblingNodesCurveTop = 10

  initialize(edges, graphLayout) {
    if (!edges || !graphLayout) {
      return false
    }

    const { height, width } = graphLayout

    this.drawEdge = this.drawEdge.bind(this)
    this.graph = graphLayout.graph
    this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")

    this.appendChild(this.svg)

    this.style.display = "block"
    this.style.height = `${height}px`
    this.style.width = `${width}px`

    this.svg.setAttribute("width", width)
    this.svg.setAttribute("height", height)
    this.svg.setAttribute("viewBox", `0 0 ${width} ${height}`)

    const augmentedEdges = edges.map(edge => this.augmentEdges(edge))
    const edgesToDraw = determineMergingEdges(augmentedEdges)

    edgesToDraw.forEach(this.drawEdge)
  }

  drawEdge(edge) {
    const svgCommand = new SVGPointConverter(edge.points).toSVGCommand()
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")

    path.setAttribute("fill", "none")
    path.setAttribute("stroke", "#e3e3e3")
    path.setAttribute("stroke-width", "2")
    path.setAttribute("d", svgCommand)

    this.svg.appendChild(path)
  }

  augmentEdges(edge) {
    const source = this.graph.node(edge.v)
    const target = this.graph.node(edge.w)

    return this.augmentEdge(source, target, edge)
  }

  augmentEdge(source, target, edge) {
    if (
      source.type == "exit-automation" ||
      source.type == "cancel-subscriber"
    ) {
      return
    }

    switch (source.kind) {
      case "condition":
        if (source.split_path) {
          return this.pathEdge(source, target, edge)
        } else {
          return this.conditionEdge(source, target, edge)
        }
      case "virtual":
        return this.virtualEdge(source, target, edge)
      default:
        return this.standardEdge(source, target, edge)
    }
  }

  // This function is similar to `conditionEdge`, but applies a fixed x-value
  // for each path that matches the target's x-value.
  pathEdge(source, target, edge) {
    const label = this.getPoints(
      source,
      `span[data-condition_path='${edge.condition_path}']`,
      () => target.x
    )
    const labelElement = source.element.querySelector(
      `span[data-condition_path='${edge.condition_path}']`
    )
    labelElement.style.left = `${target.x -
      source.x +
      target.width / 2 -
      labelElement.offsetWidth / 2}px`

    const btn = this.getPoints(
      source,
      `workflow-add-button[data-condition_path='${edge.condition_path}']`,
      () => target.x
    )
    const btnElement = source.element.querySelector(
      `workflow-add-button[data-condition_path='${edge.condition_path}']`
    )
    if (btnElement) {
      btnElement.style.left = `${target.x -
        source.x +
        target.width / 2 -
        btnElement.offsetWidth / 2}px`
    }

    const mergeBtn = this.getPoints(
      target,
      "workflow-add-button[data-type='merge']",
      this.getCenterPoint
    )

    edge.points.splice(0, 1, { ...source, y: source.y + source.height })
    edge.points.splice(1, 0, {
      x: source.x,
      y: label.y,
      kind: "path-connector"
    })

    edge.points.splice(2, 1, { ...label, kind: "condition-label" })

    if (btn) {
      edge.points.splice(3, 1, { ...btn, kind: "condition-button" })
    }

    if (mergeBtn) {
      edge.points.push({ ...mergeBtn, x: target.x, kind: "merge-point" })
    }

    edge.points.push(this.getTargetPoints(target))

    return edge
  }

  conditionEdge(source, target, edge) {
    const btnFunc = this.getConditionButtonFunction(source, target, edge)
    const labelFunc = this.getConditionLabelFunction(source, target, edge)

    const label = this.getPoints(
      source,
      `span[data-condition='${edge.condition}']`,
      labelFunc
    )

    const btn = this.getPoints(
      source,
      `workflow-add-button[data-condition='${edge.condition}']`,
      btnFunc
    )

    const mergeBtn = this.getPoints(
      target,
      "workflow-add-button[data-type='merge']",
      this.getCenterPoint
    )

    edge.points.splice(0, 1, { ...source, y: source.y + source.height })

    if (label) {
      edge.points.splice(1, 1, { ...label, kind: "condition-label" })
    }

    if (btn) {
      edge.points.splice(2, 1, { ...btn, kind: "condition-button" })
    }

    if (mergeBtn) {
      edge.points.push({ ...mergeBtn, x: target.x, kind: "merge-point" })
    }

    edge.points.push(this.getTargetPoints(target))

    return edge
  }

  standardEdge(source, target, edge) {
    const addBtn = this.getPoints(
      source,
      "workflow-add-button[data-type='child']",
      this.getCenterPoint
    )

    const mergeBtn = this.getPoints(
      target,
      "workflow-add-button[data-type='merge']",
      this.getCenterPoint
    )

    // Replace initial point by source node position
    // source.y is the top of the source node, we move it down by half its height so the line begins from the center
    edge.points.splice(0, 1, { ...source, y: source.y + source.height / 2 })

    if (addBtn) {
      edge.points.splice(1, 1, { ...addBtn, x: source.x })
    }

    if (mergeBtn) {
      edge.points.push({ ...mergeBtn, x: target.x, kind: "merge-point" })
    }

    if (addBtn && !mergeBtn) {
      // The edge is going out of a [+] but into a node instead of another [+]
      // This happens when there's 2 sibling `event` nodes

      // Adjust the Bezier curve control point so it's not too high.
      edge.points[2].y = edge.points[1].y - SVGCanvas.siblingNodesCurveTop

      if (edge.minlen > 1) {
        // For paused VAs with waiting subscribers the edge will be longer and dagre will have added extra points.
        // Remove the extra points that are too close to the curve's end point
        edge.points.splice(3, 2)
      }
      // Move last point of the curve down in case there's still not enough space
      if (edge.points[2]) edge.points[2].y += SVGCanvas.targetEdgeTranslation
    }

    // Add final point to the destination node
    edge.points.push(this.getTargetPoints(target))

    return edge
  }

  virtualEdge(source, target, edge) {
    const mergeBtn = this.getPoints(
      target,
      "workflow-add-button[data-type='merge']",
      this.getCenterPoint
    )

    edge.points.splice(0, 1, { x: source.x, y: source.y + source.height })

    if (mergeBtn) {
      edge.points.push({ ...mergeBtn, x: target.x, kind: "merge-point" })
    }

    edge.points.push(this.getTargetPoints(target))

    return edge
  }

  getTargetPoints(target) {
    if (target.kind == "virtual") {
      return { ...target, y: target.y + target.height }
    } else {
      return target
    }
  }

  getPoints(source, childSelector, xFunc) {
    const child = source.element.querySelector(childSelector)

    if (!child) {
      return false
    }

    const { offsetTop, offsetHeight, offsetLeft, offsetWidth } = child

    return {
      x: xFunc.call(this, source, offsetLeft, offsetWidth),
      y: source.y + offsetTop + offsetHeight / 2,
      height: offsetHeight,
      width: offsetWidth
    }
  }

  getConditionLabelFunction(source, target, edge) {
    if (source.conditionOrder == "no-yes") {
      return edge.condition == "yes"
        ? this.getCenterRightPoint
        : this.getCenterLeftPoint
    } else {
      return edge.condition == "yes"
        ? this.getCenterLeftPoint
        : this.getCenterRightPoint
    }
  }

  getConditionButtonFunction(source, target, edge) {
    if (source.conditionOrder == "no-yes") {
      return edge.condition == "yes"
        ? this.getOffsetRightPoint
        : this.getOffsetLeftPoint
    } else {
      return edge.condition == "yes"
        ? this.getOffsetLeftPoint
        : this.getOffsetRightPoint
    }
  }

  getCenterPoint(source, offsetLeft) {
    return source.x + Math.abs(offsetLeft) + offsetLeft / 2
  }

  getCenterLeftPoint(source, offsetLeft) {
    return source.x - Math.abs(offsetLeft) - offsetLeft / 2
  }

  getCenterRightPoint(source, offsetLeft, offsetWidth) {
    return source.x + offsetLeft / 2 - offsetWidth / 2
  }

  getOffsetLeftPoint(source, offsetLeft, offsetWidth) {
    return source.x - source.width / 2 - Math.abs(offsetLeft) + offsetWidth / 2
  }

  getOffsetRightPoint(source, offsetLeft, offsetWidth) {
    return source.x - source.width / 2 + offsetLeft + offsetWidth / 2
  }
}

customElements.get("workflow-svg") ||
  customElements.define("workflow-svg", SVGCanvas)
